import React, { useState } from "react";
import L from "leaflet";
import { buildMapsUrl } from "../utils/apiConfig";
import { 
    Icon,
    IconGroup,
    CardMeta,
    CardHeader,
    CardDescription,
    Button,
    Card,
    Modal,
    List,
    ListItem,
    Label,
    Image,
    CardContent} from 'semantic-ui-react';
import sprite from '../assets/images/numbers.svg'
import { replaceBrTags } from '../utils/stringUtils';
import SunCalc from 'suncalc';
import {
  formatDockingPointLabel,
  getEffectivePortCoordinates,
  getSelectedDockingPoint,
} from "../utils/tourPortLocations";
import { addCruiseContextToMapUrl } from '../utils/cruiseMapContinuity';

export default function PortOfCallCardTourUser({ 
    poc, 
    activeTourToShow, 
    onOpenMapInApp,
    handleWeatherClick, 
    getValidMinMax, 
    filterValidWeatherData,
    getTourEventName,
    getTourEventLocationLabel,
    getTourEventTravelSummary,
    getTourEventMarkerLabel,
    getShortWeatherSummary,
    onShowDayDetails,
    isMobile = false,
    dayFiles = null,
}) {
  const [resourcesModalOpen, setResourcesModalOpen] = useState(false);
  const visibleMaps = Array.isArray(poc?.poc_info?.maps_in_port?.features)
    ? poc.poc_info.maps_in_port.features.filter((mapItem) => {
        const mapProps = mapItem?.properties || mapItem || {};
        return mapProps?.is_visible_to_customers !== false;
      })
    : [];
  const getMapAssetPath = (mapProps, fields) => {
    for (const field of fields) {
      const value = mapProps?.[field];
      if (value) return value;
    }
    return null;
  };
  const filesForVisitDay = Array.isArray(dayFiles)
    ? dayFiles
    : (activeTourToShow?.files || []).filter(file => file.day_number === poc.visit_order);
  const resourceCount = visibleMaps.length + filesForVisitDay.length;
  const getPublicMapBaseUrl = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    return hostname === 'legacy.qrmaps.me'
      ? 'https://legacy.qrmaps.me'
      : 'https://qrmaps.me';
  };
  const getDockingMapUrl = (mapApiKey) => {
    if (!mapApiKey) return null;
    const url = new URL(`${getPublicMapBaseUrl()}/${mapApiKey}`);
    const coords = getEffectivePortCoordinates(poc);
    if (coords) {
      const selectedDock = getSelectedDockingPoint(poc);
      url.searchParams.set('dock_lon', String(Number(coords[0]).toFixed(6)));
      url.searchParams.set('dock_lat', String(Number(coords[1]).toFixed(6)));
      url.searchParams.set('dock_label', selectedDock ? formatDockingPointLabel(selectedDock) : (poc?.poc_info?.name || 'Docking location'));
    }
    return addCruiseContextToMapUrl(url.toString());
  };
  const getFileUrl = (file) => file?.download_url || file?.file_url || null;
  const openFile = (file) => {
    const fileUrl = getFileUrl(file);
    if (fileUrl) window.open(fileUrl, '_blank');
  };
  const mobileDownloadButtonStyle = {
    minWidth: '112px',
    justifyContent: 'center',
  };
  const renderResourcesModal = () => (
    <Modal
      open={resourcesModalOpen}
      onClose={() => setResourcesModalOpen(false)}
      size='small'
    >
      <Modal.Header>
        <Icon name='folder open' />
        {poc.poc_info.name}
      </Modal.Header>
      <Modal.Content scrolling style={{ maxHeight: '70vh' }}>
        {resourceCount > 0 ? (
          <div style={{ display: 'grid', gap: '8px' }}>
            {visibleMaps.map((map) => {
              const mapProps = map?.properties || {};
              const mapName = replaceBrTags(mapProps.name || mapProps.area_name) || 'Unnamed map';
              const mapArea = replaceBrTags(mapProps.country_name || mapProps.area_name || '');
              const mapApiKey = mapProps.api_key;
              const mapOnlineUrl = getDockingMapUrl(mapApiKey);
              const preferredPdfPath = getMapAssetPath(mapProps, [
                'pdf_vector_path_no_qr',
                'pdf_path_no_qr',
              ]);
              const highResJpgPath = getMapAssetPath(mapProps, [
                'jpg_200dpi_q90_path_no_qr',
                'jpg_200dpi_q90_path',
              ]);
              const lowResJpgPath = getMapAssetPath(mapProps, [
                'jpg_125dpi_q85_path_no_qr',
                'jpg_125dpi_q85_path',
              ]);
              return (
                <div
                  key={`map-${map.id || mapApiKey || mapName}`}
                  style={{
                    border: '1px solid #dbe3ee',
                    borderRadius: 8,
                    padding: '10px',
                    background: '#fff',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: isMobile ? 'center' : 'flex-start',
                    justifyContent: 'space-between',
                    gap: '10px',
                  }}>
                    <div style={{ minWidth: 0 }}>
                    <a
                      href={mapOnlineUrl || undefined}
                      style={{
                        color: '#0b6fcf',
                        fontWeight: 700,
                        textDecoration: 'none',
                        overflowWrap: 'anywhere',
                      }}
                      onClick={(event) => {
                        if (!mapOnlineUrl) event.preventDefault();
                      }}
                    >
                      {isMobile ? `Map of ${mapName}` : mapName}
                    </a>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                      {mapArea}
                    </div>
                  </div>
                    {isMobile ? (
                      <Button
                        size='mini'
                        icon='external alternate'
                        content='Open'
                        href={highResJpgPath ? buildMapsUrl(highResJpgPath) : undefined}
                        target='_blank'
                        disabled={!highResJpgPath}
                        style={mobileDownloadButtonStyle}
                      />
                    ) : null}
                  </div>
                  {!isMobile ? (
                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Button
                        size='mini'
                        icon='file pdf outline'
                        content='PDF'
                        href={preferredPdfPath ? buildMapsUrl(preferredPdfPath) : undefined}
                        target='_blank'
                        disabled={!preferredPdfPath}
                      />
                      <Button
                        size='mini'
                        icon='file image outline'
                        content='High Res JPG'
                        href={highResJpgPath ? buildMapsUrl(highResJpgPath) : undefined}
                        target='_blank'
                        disabled={!highResJpgPath}
                      />
                      <Button
                        size='mini'
                        icon='image outline'
                        content='Low Res JPG'
                        href={lowResJpgPath ? buildMapsUrl(lowResJpgPath) : undefined}
                        target='_blank'
                        disabled={!lowResJpgPath}
                      />
                      <Button
                        size='mini'
                        icon='external'
                        content='Open online'
                        onClick={() => {
                          if (mapOnlineUrl) window.location.assign(mapOnlineUrl);
                        }}
                        disabled={!mapOnlineUrl}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}

            {filesForVisitDay.map((file) => {
              const fileUrl = getFileUrl(file);
              const fileName = file.original_filename || file.name || 'Unnamed file';
              const fileTitle = file.title || file.description || file.category_name || 'Daily file';
              return (
                <div
                  key={`file-${file.id || fileName}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: '10px',
                    background: '#fff',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <button
                      type='button'
                      onClick={() => openFile(file)}
                      disabled={!fileUrl}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: fileUrl ? '#0b6fcf' : '#6b7280',
                        fontWeight: 700,
                        textAlign: 'left',
                        padding: 0,
                        minWidth: 0,
                        overflowWrap: 'anywhere',
                        cursor: fileUrl ? 'pointer' : 'default',
                      }}
                    >
                      {isMobile ? fileTitle : fileName}
                    </button>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                      {isMobile ? fileName : (file.title || file.description || file.category_name || '')}
                    </div>
                  </div>
                  <Button
                    size='mini'
                    icon='external alternate'
                    content='Open'
                    href={fileUrl || undefined}
                    target='_blank'
                    disabled={!fileUrl}
                    style={isMobile ? mobileDownloadButtonStyle : undefined}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ color: '#6b7280', fontSize: 13 }}>No maps or files linked to this visit day.</div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setResourcesModalOpen(false)}>Close</Button>
      </Modal.Actions>
    </Modal>
  );

  return (
    <React.Fragment key={poc.id}>
      <Card style={{ 
        width: '100%',
        margin: 0,
        padding: 0,
        backgroundColor: 'white',
        border: '1px solid #dbe3ee',
        borderRadius: 12,
        boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
         }}>
        <CardContent textAlign='left' style={{ padding: '12px' }}>
          <Image floated='left' size='mini'>
            <svg style={{ height: '25px', color: '#007bff' }}>
              <use href={sprite + "#N" + (poc.visit_order)} />
            </svg>
          </Image>
          <CardHeader>
            <span style={isMobile ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' } : undefined}>
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{poc.poc_info.name}</span>
              {isMobile ? (
                <Button
                  size='mini'
                  icon='info'
                  circular
                  title={`Show day details for ${poc.poc_info.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onShowDayDetails?.(poc);
                  }}
                />
              ) : null}
            </span>
            {!isMobile && (poc.holiday_info && poc.holiday_info.length > 0) ? (
              <Modal
                trigger={
                  <Button 
                    floated='right' 
                    icon='exclamation' 
                    color={poc.holiday_info[0].holiday_type.includes('National') ? 'red' : 'yellow'} 
                  />
                }
                header={
                  poc.holiday_info[0].name + " " + new Date(poc.holiday_info[0].date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  })
                }
                content={
                  poc.holiday_info[0].local_name + '. Type of holiday is: ' + poc.holiday_info[0].holiday_type + ' and it is valid for following areas: ' + poc.holiday_info[0].counties
                }
              />
            ) : null}

          </CardHeader>
          <CardMeta>
            {new Date(poc.day_arrival).toLocaleDateString('en-US', {
              weekday: "short",
              month: '2-digit',
              day: '2-digit',
            })}
            {(poc.day_arrival === poc.day_departure) ? null : " - " + new Date(poc.day_departure).toLocaleDateString('en-US', {
              weekday: "short",
              month: '2-digit',
              day: '2-digit',
            })}<br />
            <Icon name='sun' />
            {(() => {
              const date = new Date(poc.day_arrival);
              const coords = getEffectivePortCoordinates(poc);
              if (!coords) return "";
              const [lon, lat] = coords;
              const timezone = poc.poc_info.timezone || "Europe/Berlin";
              const sunTimes = SunCalc.getTimes(date, lat, lon);
              const sunrise = sunTimes?.sunrise;

              if (!sunrise || isNaN(sunrise)) return ""; // defensive guard
              try {
                return new Intl.DateTimeFormat([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: timezone,
                }).format(sunrise);
              } catch {
                // Fallback if timezone is invalid
                return sunrise.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
              }
            })()}
            <Icon name='moon' />
            {(() => {
              const date = new Date(poc.day_arrival);
              const coords = getEffectivePortCoordinates(poc);
              if (!coords) return "";
              const [lon, lat] = coords;
              const timezone = poc.poc_info.timezone || "Europe/Berlin";
              const sunTimes = SunCalc.getTimes(date, lat, lon);
              const sunset = sunTimes?.sunset;

              if (!sunset || isNaN(sunset)) return ""; // defensive guard
              try {
                return new Intl.DateTimeFormat([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: timezone,
                }).format(sunset);
              } catch {
                // Fallback if timezone is invalid
                return sunset.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
              }
            })()}
            <br />
            {poc.weather && poc.weather.hourly ? (
              <p
                className="TourUser-port-weather-row"
                onClick={() => handleWeatherClick(poc)}
                style={{ 
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                {(() => {
                  const tempRange = getValidMinMax(poc.weather.hourly.temperature_2m);
                  const validPrecip = filterValidWeatherData(poc.weather.hourly.precipitation);
                  const validCloudCover = filterValidWeatherData(poc.weather.hourly.cloud_cover);
                  const validWindSpeed = filterValidWeatherData(poc.weather.hourly.wind_speed_10m);
                  const validWindDir = filterValidWeatherData(poc.weather.hourly.wind_direction_10m);
                  const validUV = filterValidWeatherData(poc.weather.hourly.uv_index);
                  
                  return (
                    <>
                      {tempRange.min !== null ? `${tempRange.min.toFixed(0)}-${tempRange.max.toFixed(0)}°C` : 'No forecast data'}
                      
                      {validPrecip.length > 0 && (
                        <span style={{ color: validPrecip.reduce((a, b) => a + b, 0) > 1 ? 'blue' : 'inherit' }}>
                          <Icon name='rain' />
                          {Math.round(validPrecip.reduce((a, b) => a + b, 0))}mm
                        </span>
                      )}
                      {validCloudCover.length > 0 && (
                        <>
                          <Icon name='cloud' />
                          {Math.round(validCloudCover.reduce((acc, c) => acc + c, 0) / validCloudCover.length)}%
                        </>
                      )}
                      {validWindSpeed.length > 0 && validWindDir.length > 0 && (
                        <span style={{ color: Math.round(Math.max(...validWindSpeed) * 0.539957) > 15 ? 'red' : 'inherit' }}>
                          <Icon 
                            name='arrow down' 
                            style={{ 
                              transform: `rotate(${Math.round(validWindDir.reduce((acc, dir) => acc + dir, 0) / validWindDir.length)}deg)` 
                            }}
                          />  
                          {Math.round(Math.max(...validWindSpeed) * 0.539957)}kt
                        </span>
                      )}
                      {validUV.length > 0 && (
                        <span style={{ 
                          color: Math.max(...validUV) >= 8 ? 'red' : 
                                   Math.max(...validUV) >= 6 ? 'orange' : 
                                   Math.max(...validUV) >= 3 ? 'yellow' : 'inherit' 
                        }}>
                          <Icon name='sun' />
                          UV {Math.max(...validUV).toFixed(1)}
                        </span>
                      )}
                      <Icon name='chart line' style={{ marginLeft: '5px', opacity: 0.7 }} />
                    </>
                  );
                })()}
              </p>
            ) : null}
            

          </CardMeta>
          <CardDescription>
            {poc.events && poc.events.filter((event) => (event?.placement || 'port') === 'port').length > 0 ? (
              <List>
                {poc.events
                  .filter((event) => (event?.placement || 'port') === 'port')
                  .map((event, eventIndex) => {
                    const travel = getTourEventTravelSummary?.(event, poc);
                    const mode = travel?.mode || event?.route_mode || 'road';
                    const label = getTourEventMarkerLabel?.(event, poc, eventIndex);
                    const eventName = getTourEventName?.(event) || event?.event_info?.name || event?.title || 'Tour event';
                    const locationLabel = getTourEventLocationLabel?.(event);
                    const eventCountry = event?.location_country?.iso3_code || event?.event_info?.location_country?.iso3_code;
                    const portCountry = poc?.poc_info?.country?.iso3_code;
                    return (
                      <ListItem key={event.id}>
                        {label ? (
                          <Label size='mini' color='teal' circular style={{ marginRight: 4 }}>
                            {label}
                          </Label>
                        ) : null}
                        {eventName}
                        {locationLabel ? (
                          <span style={{ color: '#64748b', marginLeft: 4 }}>({locationLabel})</span>
                        ) : null}
                        {travel ? (
                          <div style={{ color: '#64748b', fontSize: 11, marginLeft: 28 }}>
                            <Icon name={mode === 'road' ? 'car' : mode === 'river' ? 'ship' : 'anchor'} />
                            {travel.distanceLabel}
                            {travel.timeLabel !== '—' ? `, ${travel.timeLabel}` : ''}
                          </div>
                        ) : null}
                        {getShortWeatherSummary?.(event.weather) ? (
                          <div style={{ color: '#64748b', fontSize: 11, marginLeft: 28 }}>
                            <Icon name='cloud' />
                            {getShortWeatherSummary(event.weather)}
                          </div>
                        ) : null}
                        {eventCountry && portCountry && eventCountry !== portCountry && event.holiday_info?.length > 0 ? (
                          <Modal
                            trigger={
                              <Button
                                floated='right'
                                icon='exclamation'
                                color={event.holiday_info[0].holiday_type.includes('National') ? 'red' : 'yellow'}
                              />
                            }
                            header={
                              eventName + " " + new Date(event?.date || event?.event_info?.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                              })
                            }
                            content={
                              <List bulleted style={{ margin: '30px' }}>
                                {event.holiday_info.map(holiday => (
                                  <ListItem key={holiday.id}>
                                    {new Date(holiday.date).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                    }) + ' - ' + holiday.local_name + '. Type of holiday is: ' + holiday.holiday_type + ' and it is valid for following areas: ' + holiday.counties}
                                  </ListItem>
                                ))}
                              </List>
                            }
                          />
                        ) : null}
                      </ListItem>
                    );
                  })}
              </List>
            ) : null}

                      </CardDescription>

            {renderResourcesModal()}
            
            {/* Unified Action Buttons Section */}
            {!isMobile && <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 0 0',
              borderTop: '1px solid #eef2f7',
              marginTop: '12px'
            }}>
              <Button
                size='mini'
                icon='folder open'
                label={{
                  basic: true,
                  content: resourceCount,
                  color: resourceCount > 0 ? 'blue' : 'grey'
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  setResourcesModalOpen(true);
                }}
                disabled={resourceCount === 0}
                title={resourceCount > 0 ? `View ${resourceCount} map/file resource(s) for ${poc.poc_info.name}` : 'No maps or files available'}
              />
            </div>}
          </CardContent>
        </Card>
    </React.Fragment>
  );
}
